import { catchError, of, switchMap } from 'rxjs';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { BrandingService } from '@app/services/branding.service';
import { GuestSessionService } from '@app/services/guest-session.service';
import { SettingsService } from '@app/services/settings.service';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-branding-logos',
  standalone: true,
  imports: [TranslocoPipe],
  templateUrl: './branding-logos.component.html',
  styleUrls: ['./branding-logos.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BrandingLogosComponent {
  readonly showPoweredBy = input<boolean>(false);
  readonly creditOnly = input<boolean>(false);
  /** Render only the logo, never the "powered by" credit (e.g. when a sibling
   * instance already shows the credit). */
  readonly logoOnly = input<boolean>(false);
  readonly deviceUuid = input<string | null>(null);
  readonly forceDark = input<boolean>(false);

  private readonly brandingService = inject(BrandingService);
  private readonly qrUserService = inject(GuestSessionService);
  private readonly settingsService = inject(SettingsService);

  private readonly resolvedDeviceUuid = computed(
    () => this.deviceUuid() ?? this.qrUserService.getDeviceUuid(),
  );

  private readonly branding = toSignal(
    toObservable(this.resolvedDeviceUuid).pipe(
      switchMap((uuid) =>
        uuid ? this.brandingService.getBranding(uuid).pipe(catchError(() => of(null))) : of(null),
      ),
    ),
  );

  readonly loaded = computed(() => this.branding() !== undefined);
  readonly tenantLogo = computed(() => this.branding()?.tenant_logo_qr ?? null);
  private readonly settingsDarkMode = toSignal(this.settingsService.darkMode$, {
    initialValue: false,
  });
  readonly isDarkMode = computed(() => this.forceDark() || this.settingsDarkMode());
}
